/**
 * Smooth vertical gradients for the menu scenes.
 *
 * Phaser's Graphics has no gradient fill, so this ramps through enough bands to
 * read as continuous. Drawing three wide bands leaves visible hard edges, which
 * is exactly what this replaces.
 */
import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../types';

export type GradientStop = readonly [color: number, position: number];

export interface GradientOptions {
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
  /** More bands is smoother; 64 is indistinguishable from a real gradient. */
  readonly bands?: number;
  readonly depth?: number;
  readonly scrollFactor?: number;
}

function sampleStops(stops: readonly GradientStop[], t: number): number {
  if (stops.length === 0) return 0x000000;
  const first = stops[0];
  if (first === undefined) return 0x000000;
  if (t <= first[1]) return first[0];

  for (let i = 1; i < stops.length; i += 1) {
    const previous = stops[i - 1];
    const current = stops[i];
    if (previous === undefined || current === undefined) continue;
    if (t <= current[1]) {
      const span = current[1] - previous[1];
      const local = span <= 0 ? 0 : (t - previous[1]) / span;
      const blended = Phaser.Display.Color.Interpolate.ColorWithColor(
        Phaser.Display.Color.ValueToColor(previous[0]),
        Phaser.Display.Color.ValueToColor(current[0]),
        100,
        Math.round(local * 100),
      );
      return Phaser.Display.Color.GetColor(blended.r, blended.g, blended.b);
    }
  }
  return stops[stops.length - 1]?.[0] ?? 0x000000;
}

export function drawVerticalGradient(
  scene: Phaser.Scene,
  stops: readonly GradientStop[],
  options: GradientOptions = {},
): Phaser.GameObjects.Graphics {
  const x = options.x ?? 0;
  const y = options.y ?? 0;
  const width = options.width ?? GAME_WIDTH;
  const height = options.height ?? GAME_HEIGHT;
  const bands = Math.max(8, options.bands ?? 64);

  const graphics = scene.add.graphics();
  const bandHeight = height / bands;
  for (let i = 0; i < bands; i += 1) {
    const t = bands === 1 ? 0 : i / (bands - 1);
    graphics.fillStyle(sampleStops(stops, t), 1);
    // The +1 overlap hides seams from fractional band heights.
    graphics.fillRect(x, y + i * bandHeight, width, bandHeight + 1);
  }

  if (options.depth !== undefined) graphics.setDepth(options.depth);
  if (options.scrollFactor !== undefined) graphics.setScrollFactor(options.scrollFactor);
  return graphics;
}

/** The night-sky wash the menus share. */
export const MENU_GRADIENT: readonly GradientStop[] = [
  [0x0b1220, 0],
  [0x16233a, 0.45],
  [0x1d3557, 0.78],
  [0x2b1b45, 1],
];
